<template>
  <div class="categories-list relative mt-2 pl-2">
    <button
      :id="'js-button-box-toggle-' + listName"
      type="button"
      class="button-box mt-1 pl-2 truncate-text"
      :aria-label="t('tts.settings.voiceSelector')"
      :title="t('tts.settings.voiceSelector')"
      @click="toggleItemsBox"
    >
      {{ findCurrentSelectedLabel() }}
    </button>

    <div
      :id="'js-custom-items-box-' + listName"
      class="items-list flex-wrap"
      role="radiogroup"
      :aria-label="t('settings.radioGroupSelect') + t('tts.settings.voice')"
    >
      <template v-for="list in itemsList" :key="list.label">
        <h3 class="items-list__category-title font-bold pt-2 pb-1 pl-3">
          {{ list.label }}
        </h3>
        <template v-for="item in list.options" :key="item.value">
          <input
            :id="list.label + '-' + item.value"
            v-model="model"
            type="radio"
            class="hide-input"
            :class="item.active ? 'hide-input--active' : ''"
            :name="listName"
            :value="item.value"
            :tabindex="selectionEnabled ? '0' : '-1'"
            @keypress.enter="toggleItemsBox"
            @keydown.esc="toggleItemsBox"
          />
          <label
            :for="list.label + '-' + item.value"
            class="item-square-selector relative p-2 flex items-center block px-6 py-2 border-b w-full focus:outline-none focus:ring-0 transition duration-200 cursor-pointer"
            :title="item.label"
            @mouseup="toggleItemsBox"
          >
            <span class="truncate-text">{{ item.label }}</span>
          </label>
        </template>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import { categorySetting } from "@/helpers/types";

const { t } = useI18n();
const selectionEnabled = ref<boolean>(false);

const model = defineModel<string>();

const props = defineProps({
  itemsList: {
    type: Array as PropType<Array<categorySetting>>,
    default: null,
  },
  listName: {
    type: String,
    default: "",
  },
});

function toggleItemsBox(e: Event) {
  e.stopImmediatePropagation();
  const toggleButton = document.getElementById(
    "js-button-box-toggle-" + props.listName
  );
  const itemsBox = document.getElementById(
    "js-custom-items-box-" + props.listName
  );
  const items = document.getElementsByName(props.listName);
  const getItemActive = Array.from(items)?.find((item: HTMLElement) => {
    return item.classList.contains("hide-input--active");
  });

  if (!toggleButton || !itemsBox) return;

  toggleButton.classList.toggle("button-box--open");
  itemsBox.classList.toggle("items-list--show");

  if (itemsBox.classList.contains("items-list--show")) {
    selectionEnabled.value = true;
    const activeItem = getItemActive as HTMLInputElement;
    if (activeItem?.labels?.[0]) {
      activeItem.labels[0].focus();
      activeItem.labels[0].scrollIntoView({});
    } else {
      items?.item(0)?.focus();
    }
  } else {
    toggleButton.focus();
    selectionEnabled.value = false;
  }
}

function findCurrentSelectedLabel() {
  let labelSelected = undefined;
  const currentSelected = props.itemsList?.find((list) => {
    return list.options?.find((item) => {
      if (item.value === model.value) {
        labelSelected = item.label;
        return true;
      }
      return item.value === model.value;
    });
  });
  if (!currentSelected) return model.value;
  return labelSelected || model.value;
}
</script>

<style lang="scss" scoped>
@use "@/assets/styles/reader.scss" as *;

.categories-list {
  max-width: 18rem;
  font-size: 0.85rem;

  .button-box {
    position: relative;
    width: 100%;
    max-width: 15rem;
    height: 3rem;
    text-align: left;
    padding: 0 2rem 0 1rem;
    border: $spacingXS solid var(--DM__theme-hover-color);
    &::after {
      content: "expand_more";
      position: absolute;
      width: $spacingL;
      right: $spacingM;
      top: 50%;
      transform: translateY(-50%);
      z-index: -1;
      font-size: 1.2rem;
      font-family: "Material Icons";
      font-feature-settings: "liga";
      -webkit-font-feature-settings: "liga";
      transition: all 0.2s;
    }
    @media (any-hover: hover) {
      &:hover {
        border-color: var(--DM__theme-slider-color);
      }
    }
    &:focus {
      border-color: var(--DM__theme-slider-color);
      outline: 0;
      &:not(:focus-visible) {
        border-color: var(--DM__theme-slider-color);
      }
    }
    &--open {
      &::after {
        transform: translateY(-52%) rotate(180deg);
      }
    }
  }

  .items-list {
    display: flex;
    flex-wrap: wrap;
    width: 15rem;
    top: 5%;
    left: 35px;
    max-height: 0;
    border: $spacingXS solid transparent;
    border-top: none;
    overflow: hidden;
    overscroll-behavior: contain;
    background-color: var(--DM__theme-fill-color);
    z-index: 2;
    transition:
      opacity 0.3s,
      max-height 0.3s;
    opacity: 0;

    @include scrollBar();

    &--show {
      transition:
        opacity 0.5s,
        max-height 0.5s;
      max-height: 200px;
      overflow-y: auto;
      border-color: var(--DM__theme-hover-color);
      opacity: 1;
      @media (min-width: $screenSM) {
        max-height: 50vh;
      }
    }

    &__category-title {
      width: 100%;
      background-color: var(--DM__theme-hover-color);
      color: var(--DM__theme-font-color);
      opacity: 0.75;
    }
  }

  .hide-input {
    opacity: 0;
    position: absolute;
    z-index: -1;
    &--active + .item-square-selector {
      border-left: 4px solid var(--DM__theme-slider-color);
    }
  }

  .item-square-selector {
    height: 2.5rem;
    width: 100%;
    border-color: var(--DM__theme-separator-color);
    text-decoration-color: var(--DM__theme-slider-color);
    @media (any-hover: hover) {
      &:hover {
        border-left: 4px solid var(--DM__theme-slider-color);
        background-color: #f9fafb;
        color: #1f2937;
      }
    }
  }

  .hide-input:focus + .item-square-selector {
    text-decoration: underline;
    text-decoration-color: var(--DM__theme-slider-color);
    text-underline-offset: 3px;
    text-decoration-thickness: 2px;
  }

  .truncate-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
